import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  locale: string;
  teachersDrawerOpen: boolean;
  timetableStatusMessage: string | null;
  timetableStatusTimestamp: Date | null;
}

interface UIActions {
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setLocale: (locale: string) => void;
  toggleTeachersDrawer: () => void;
  setTeachersDrawerOpen: (open: boolean) => void;
  setTimetableStatus: (message: string | null, timestamp?: Date | null) => void;
  clearTimetableStatus: () => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      theme: 'light',
      sidebarOpen: true,
      locale: 'en',
      teachersDrawerOpen: false,
      timetableStatusMessage: null,
      timetableStatusTimestamp: null,
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'light' ? 'dark' : 'light',
        })),
      setTheme: (theme) => set({ theme }),
      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setLocale: (locale) => set({ locale }),
      toggleTeachersDrawer: () =>
        set((state) => ({ teachersDrawerOpen: !state.teachersDrawerOpen })),
      setTeachersDrawerOpen: (open) => set({ teachersDrawerOpen: open }),
      setTimetableStatus: (message, timestamp = new Date()) =>
        set({ timetableStatusMessage: message, timetableStatusTimestamp: timestamp }),
      clearTimetableStatus: () =>
        set({ timetableStatusMessage: null, timetableStatusTimestamp: null }),
    }),
    {
      name: 'ui-storage',
      onRehydrateStorage: () => (state) => {
        // JSON serialization loses the Date type — restore it on rehydration
        if (state?.timetableStatusTimestamp) {
          state.timetableStatusTimestamp = new Date(state.timetableStatusTimestamp);
        }
      },
    }
  )
);
